/**
 * Shared dashboard color utilities for consistent styling across web and mobile.
 *
 * Neutral surfaces are theme-aware: on web they resolve the theme CSS variable
 * (so they switch with light/dark). React Native has no CSS variables, so a
 * mobile caller passes its active palette in via the `neutrals` argument —
 * without it these functions fall back to the dark literal, which is what made
 * the section headers stay black in the app's light mode.
 * Semantic status colors stay literal on purpose — they're used as SVG
 * icon colors, where a CSS variable can't resolve, and they read on both grounds.
 * `themed()` mirrors the same var()-with-dark-fallback contract as theme.colors.*
 * without taking a dependency on the ui package (which builds after this one).
 */
const isWeb = typeof document !== 'undefined';
const themed = (cssVar, darkLiteral) => (isWeb ? `var(${cssVar}, ${darkLiteral})` : darkLiteral);
// Cycle-time status palette — literal, legible on both grounds.
const CYCLE_GREEN = '#10B981'; // Emerald-500 — good cycle time (< 5 days)
const CYCLE_AMBER = '#F59E0B'; // Amber-500 — warning cycle time (5-10 days)
const CYCLE_RED = '#EF4444'; // Red-500 — poor cycle time (> 10 days)
const CYCLE_GRAY = '#6B7280'; // Gray-500 — no data (reads on light and dark)
// Neutral surfaces — theme tokens (switch on web, dark literal on mobile).
const DEFAULT_ROW_ACCENT = themed('--tc-border-light', '#333333');
/** Ink & Ember scarlet. Same hue in both themes, so it is a literal on purpose. */
const BRAND_EMBER = '#E3522E';
// ─── Internal helpers ──────────────────────────────────────────────────────
/**
 * Combine a 6-character hex color with an alpha value into an 8-character hex string.
 * Avoids `rgba()` which behaves differently across platforms.
 */
function hexWithAlpha(hex, alpha) {
    const clean = hex.replace('#', '').substring(0, 6);
    const alphaHex = Math.round(Math.min(1, Math.max(0, alpha)) * 255)
        .toString(16)
        .padStart(2, '0');
    return `#${clean}${alphaHex}`;
}
// ─── Public API ────────────────────────────────────────────────────────────
/**
 * Returns a color for cycle time based on percentage of target.
 * Input is in minutes (matches DashboardDepartment.average_cycle_time).
 * Target is in days (matches DashboardDepartment.target_cycle_time_days).
 *
 * - <= 100% of target: green (on track)
 * - 100-200% of target: amber (over target)
 * - > 200% of target: red (critically over)
 * - null/undefined: gray (no data)
 */
export function getCycleTimeColor(minutes, targetDays) {
    const tone = getCycleTimeTone(minutes, targetDays);
    return tone === 'good' ? CYCLE_GREEN : tone === 'warn' ? CYCLE_AMBER : tone === 'bad' ? CYCLE_RED : CYCLE_GRAY;
}
/**
 * getCycleTimeColor's bands as a word, for a screen that paints them with
 * its own theme tokens (success / warning / danger) instead of the literals.
 * Same thresholds: up to the target good, up to twice it warn, beyond bad;
 * no time none.
 *
 * The target follows the server's rule (executive_kpi_sql.go): a department
 * has a target when target_cycle_time_days is set and above 0. `null`, 0 or
 * less is no target: `none`, never "over" (the server's past-due fallback,
 * twice the usual visit or 5 days, is not a goal to colour against). Leaving
 * the argument out (a caller with no department, e.g. a work order card)
 * keeps the old 5-day yardstick.
 */
export function getCycleTimeTone(minutes, targetDays) {
    if (minutes == null || isNaN(Number(minutes)))
        return 'none';
    if (targetDays === null || (targetDays !== undefined && !(targetDays > 0)))
        return 'none';
    const target = targetDays ?? 5;
    const targetMinutes = target * 1440;
    const ratio = Number(minutes) / targetMinutes;
    if (ratio <= 1)
        return 'good';
    if (ratio <= 2)
        return 'warn';
    return 'bad';
}
/**
 * Colors for a work order count badge.
 *
 * "Ink & Ember" again: the actionable number is what carries the accent — the
 * section's own color when it has one, otherwise the brand scarlet. Zero stays
 * muted. Matches the web dashboard's count badge exactly.
 */
export function getCountBadgeColor(count, sectionColor, neutrals) {
    // Zero is not actionable, so it gets no accent at all.
    if (count === 0) {
        return {
            backgroundColor: neutrals?.backgroundHover ?? themed('--tc-background-quaternary', '#2A2A2A'),
            textColor: themed('--tc-text-tertiary', '#999999'),
        };
    }
    // A count worth acting on: the section's own color when set, otherwise the
    // brand ember. One tint step (~14%), not two — the old 20%/40% split made a
    // busy section shout louder than a quiet one for no useful reason.
    const accent = sectionColor || BRAND_EMBER;
    return {
        backgroundColor: hexWithAlpha(accent, 0.14),
        textColor: accent,
    };
}
/**
 * The four standard sections' default colours — the same the API gives a new
 * location (location_section_default_colors.go) and migration 000350 gave the
 * existing ones. The section editor offers them first among its swatches.
 * Calm colours that run cool to warm along the shop flow, so the brand ember
 * stays the one loud colour (Ready to invoice, past due).
 */
export const STANDARD_SECTION_COLORS = {
    'Inspection & Pricing': '#5B87C7',
    Approvals: '#8B6FD6',
    // Teal, not gold (Alex, 2026-10-25): amber is for Past due and late only.
    Production: '#3A9E9A',
    // Gray, not sage (Alex, 2026-10-25): sage sat too close to Production's teal.
    Closed: '#7A8291',
};
/**
 * The section editor's colour swatches, in order: the four standard colours,
 * then a few more in the same calm tone (no red: it reads as an alert next to
 * the brand ember). Gold comes last: it sits close to the amber of Past due
 * and late, so it isn't a standard colour. Lowercase `#rrggbb`, as
 * normalizeHexColor returns.
 */
export const SECTION_COLOR_SWATCHES = [
    { name: 'Steel blue', hex: '#5b87c7' },
    { name: 'Violet', hex: '#8b6fd6' },
    { name: 'Teal', hex: '#3a9e9a' },
    { name: 'Gray', hex: '#7a8291' },
    { name: 'Sage', hex: '#4fa36b' },
    { name: 'Rose', hex: '#c2577a' },
    { name: 'Gold', hex: '#d4a23a' },
];
/**
 * A new section's colour when the shop hasn't picked one: steel blue, the
 * standard Inspection colour. Calm, so the brand ember stays for what needs
 * action. "No color" is still a choice in the editor.
 */
export const DEFAULT_SECTION_COLOR = '#5b87c7';
/** A section's standard colour, else the default steel blue. */
export function defaultSectionColor(name) {
    return STANDARD_SECTION_COLORS[(name || '').trim()] ?? DEFAULT_SECTION_COLOR;
}
/**
 * A section colour as `#rrggbb`, or null when it is not a hex colour. Older
 * sections were saved as shorthand (`#abc`) and some as `#rrggbbaa`; the
 * colour picker writes `#rrggbb`. Callers that add an alpha (`${hex}38`) need
 * the six-digit form.
 */
export function normalizeHexColor(color) {
    const m = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.exec((color || '').trim());
    if (!m)
        return null;
    const hex = m[1].length === 3 ? m[1].replace(/./g, (c) => c + c) : m[1].substring(0, 6);
    return `#${hex.toLowerCase()}`;
}
/**
 * Ink for a mark drawn on a section's own colour (its icon tile): white on a
 * dark or mid colour, near-black on a light one (yellow, light green), so the
 * icon stays readable whatever colour the shop picked. Uses WCAG relative
 * luminance; a missing or malformed value falls back to white.
 */
export function inkOnColor(hex) {
    const normalized = normalizeHexColor(hex);
    if (!normalized)
        return '#FFFFFF';
    const clean = normalized.substring(1);
    const channel = (i) => {
        const c = parseInt(clean.substring(i, i + 2), 16) / 255;
        return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    };
    const luminance = 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);
    // White while it keeps the 3:1 contrast WCAG asks of an icon (luminance up
    // to ~0.30 — brand scarlet, purple, blue, green); near-black beyond that.
    return 1.05 / (luminance + 0.05) >= 3 ? '#FFFFFF' : '#111111';
}
/**
 * Returns an accent color for a department row left border.
 * Uses the section color directly, or a neutral fallback.
 */
export function getDepartmentRowAccent(sectionColor, neutralAccent) {
    return sectionColor || neutralAccent || DEFAULT_ROW_ACCENT;
}
/**
 * Determines cycle time trend direction by comparing current vs previous period.
 * For cycle time, LOWER is better (faster service).
 *
 * Uses a 5% threshold band for "stable" to avoid noise.
 */
export function getCycleTimeTrend(currentMinutes, previousMinutes) {
    if (currentMinutes == null ||
        previousMinutes == null ||
        isNaN(Number(currentMinutes)) ||
        isNaN(Number(previousMinutes)) ||
        previousMinutes === 0) {
        return 'unknown';
    }
    const percentChange = (Number(currentMinutes) - Number(previousMinutes)) / Math.abs(Number(previousMinutes));
    if (Math.abs(percentChange) < 0.05)
        return 'stable';
    if (percentChange < 0)
        return 'improving';
    return 'worsening';
}
/**
 * Fallback: maps the efficiency_rating field to a trend direction.
 * Used when previous_average_cycle_time is not available from the API.
 */
export function getCycleTimeTrendFromRating(efficiencyRating) {
    switch (efficiencyRating) {
        case 'good':
            return 'improving';
        case 'poor':
            return 'worsening';
        case 'average':
            return 'stable';
        default:
            return 'unknown';
    }
}
/**
 * Returns the color for a trend indicator arrow/icon.
 */
export function getTrendColor(trend) {
    switch (trend) {
        case 'improving':
            return CYCLE_GREEN;
        case 'worsening':
            return CYCLE_RED;
        case 'stable':
            return CYCLE_GRAY;
        default:
            return CYCLE_GRAY;
    }
}
/**
 * Resolves the cycle time trend for a department, preferring numeric comparison
 * over efficiency_rating fallback.
 */
export function resolveDepartmentTrend(dept) {
    const numericTrend = getCycleTimeTrend(dept.average_cycle_time, dept.previous_average_cycle_time);
    if (numericTrend !== 'unknown')
        return numericTrend;
    return getCycleTimeTrendFromRating(dept.efficiency_rating);
}
